import { Box, FormControl, FormControlLabel, Radio, RadioGroup, Typography, TextField } from '@mui/material';
import { ListingOnboardingStepProps, ListingFormData } from '../../types/listing_onboarding'
import { useEffect, useState } from 'react';
import NotificationBanner from '../../components/NotificationBanner';

const ListingOnboardingStep3: React.FC<ListingOnboardingStepProps> = ({ formData, setFormData, setStepValid, listingCredit, paymentStatus }) => {
    const [showErrorBanner, setShowErrorBanner] = useState(paymentStatus === 'failed' || false);
    const [showSuccessBanner, setShowSuccessBanner] = useState(paymentStatus === 'success' || false);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [touched, setTouched] = useState<{ [key: string]: boolean }>({});

    // Add validation function
    const validateForm = () => {
        const newErrors: { [key: string]: string } = {};

        // Only show application URL error if field has been touched
        if (touched.application_url) {
            if (!formData.application_url || formData.application_url.trim() === '') {
                newErrors.application_url = 'Application URL is required.';
            }
        }

        setErrors(newErrors);
    };

    // Run validation whenever form data or touched state changes
    useEffect(() => {
        validateForm();
    }, [formData, touched]);

    useEffect(() => {
        setStepValid(true);
        setShowErrorBanner(paymentStatus === 'failed' || false);
        setShowSuccessBanner(paymentStatus === 'success');
    }, [paymentStatus]);

    const handleUnitOccupiedChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedData: ListingFormData = {
            ...formData,
            unit_occupied: event.target.value == "true"
        };
        setFormData(updatedData)
    }

    const handleGroupShowingsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const updatedData: ListingFormData = {
            ...formData,
            group_showings: event.target.value == "true"
        };
        setFormData(updatedData)
    }

    const handleApplicationUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newUrl = event.target.value;

        // Update form data
        setFormData(prevData => ({
            ...prevData,
            application_url: newUrl
        }));

        // Validate the input immediately if field has been touched
        if (touched.application_url) {
            const newErrors = { ...errors };

            if (!newUrl || newUrl.trim() === '') {
                newErrors.application_url = 'Application URL is required.';
            } else {
                delete newErrors.application_url; // Clear the error if validation passes
            }

            setErrors(newErrors);
        }
    };

    const handleApplicationUrlBlur = () => {
        // Mark field as touched when user leaves the field
        setTouched(prev => ({ ...prev, application_url: true }));
    };

    return (
        <>
            <NotificationBanner
                open={showErrorBanner}
                type="error"
                message="Your payment failed. Please try again."
                onClose={() => setShowErrorBanner(false)}
            />
            <NotificationBanner
                open={showSuccessBanner}
                type="success"
                message="Your payment was successful. You can now create your listing."
                onClose={() => setShowSuccessBanner(false)}
            />

            <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                <FormControl component="fieldset" sx={{ width: '100%' }}>
                    <Typography variant="body1" sx={{mt: '1.5rem' , mb: '0.8rem', fontWeight: 'bold'}} gutterBottom>
                        Is the unit occupied? AI will notify tenants 24 hours before tours.
                    </Typography>
                    <RadioGroup
                        name="is_occupied"
                        value={formData.unit_occupied?.toString() || (formData.rental_type === 'entire_place' ? 'false' : 'true')}
                        onChange={handleUnitOccupiedChange}
                        sx={{mb: '0.8rem'}}
                    >
                        <FormControlLabel value="false" control={<Radio />} label="No, the unit is empty." />
                        <FormControlLabel value="true" control={<Radio />} label="Yes, the unit is occupied." />
                    </RadioGroup>
                    {formData.unit_occupied && (
                        <TextField
                            fullWidth
                            id="tenant_contact"
                            name="tenant_contact"
                            placeholder="Enter tenant email or phone number"
                            value={formData.current_tenant_contact || ''}
                            onChange={(e) => setFormData(prev => ({
                                ...prev,
                                current_tenant_contact: e.target.value
                            }))}

                            sx={{
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                        borderColor: 'grey.400',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: 'grey.500',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: 'black',
                                    },
                                },
                            }}
                        />
                    )}
                    <Typography variant="body1" gutterBottom sx={{ mt: '1.5rem', mb: '0.8rem', fontWeight: 'bold'}}>
                        Want to save time with group showings? We will automatically cluster showings for you.
                    </Typography>
                    <RadioGroup
                        name="group_showings"
                        value={formData.group_showings?.toString() || 'true'}
                        onChange={handleGroupShowingsChange}
                    >
                        <FormControlLabel value="true" control={<Radio />} label="Yes please!" />
                        <FormControlLabel value="false" control={<Radio />} label="No, I prefer one-on-one showings." />
                    </RadioGroup>

                    <Typography variant="body1" gutterBottom sx={{ mt: '2.4rem', mb: '0.8rem',  fontWeight: 'bold'}}>
                        Where should prospects apply? Use our free option or customize it!
                    </Typography>
                    <TextField
                        fullWidth
                        id="application_url"
                        name="application_url"
                        defaultValue="apply.heyyproperty.com"
                        value={formData.application_url}
                        onChange={handleApplicationUrlChange}
                        onBlur={handleApplicationUrlBlur}
                        error={!!errors.application_url}
                        helperText={errors.application_url}
                        FormHelperTextProps={{
                            sx: {
                                fontFamily: 'Inter, sans-serif',
                                fontSize: '0.75rem',
                            }
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                '& fieldset': {
                                    borderColor: 'grey.400',
                                },
                                '&:hover fieldset': {
                                    borderColor: 'grey.500',
                                },
                                '&.Mui-focused fieldset': {
                                    borderColor: 'black',
                                },
                            },
                        }}
                    />
                </FormControl>
            </Box>
        </>
    );
};

export default ListingOnboardingStep3;